import { Box, HStack, Image, Link, Text, VStack } from '@chakra-ui/react';
import { ReactNode } from 'react';

import svgFiles from '@/assets/svg/svgFiles';
import { READINESS_DOMAIN, WELLNESS_DOMAINS_DISPLAY_LABELS, WELLNESS_DOMAINS_LIST } from '@/constants';
import { LOCAL_US_STATES } from '@/constants/stringVars';
import type { TContentCreatorApplicationSummary } from '@/services/api/requests/contentCreatorApplications';

export const MAX_APPLICANT_INFO_CARD_WIDTH = '680px';

const contentFocusOptions = [
  ...WELLNESS_DOMAINS_LIST.map((domain) => ({
    label: WELLNESS_DOMAINS_DISPLAY_LABELS[domain],
    value: domain,
  })),
  { label: 'Readiness', value: READINESS_DOMAIN },
];

export const formatContentCreatorApplicationDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Not provided';

const getContentFocusLabel = (value: string) =>
  contentFocusOptions.find((option) => option.value === value)?.label ?? value;

const getStateLabel = (stateCode?: string) =>
  stateCode ? (LOCAL_US_STATES.find((USState) => USState.code === stateCode)?.label ?? stateCode) : '';

export const ApplicationDetailSection = ({
  label,
  labelColor,
  children,
  spacing = '0px',
}: {
  label: string;
  children: ReactNode;
  labelColor?: string;
  spacing?: string;
}) => (
  <VStack alignItems={'flex-start'} spacing={spacing}>
    <Text color={labelColor ? labelColor : 'background.blueGray'} fontSize={'11px'} lineHeight={'14px'} fontWeight={'800'}>
      {label}
    </Text>
    {children}
  </VStack>
);

const ApplicantApplicationInfoCard = ({
  application,
  dateLabel,
  dateValue,
}: {
  application: TContentCreatorApplicationSummary;
  dateLabel?: string;
  dateValue?: string | null;
}) => {
  const profile = application.profile;
  const applicantName = [application.applicantName?.firstName, application.applicantName?.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  const locationParts = [profile.location?.city, getStateLabel(profile.location?.state), profile.location?.zip]
    .map((part) => part?.trim())
    .filter(Boolean);
  const contentFocus = profile.contentFocus?.map(getContentFocusLabel).join(', ');

  return (
    <VStack
      width={'100%'}
      maxWidth={MAX_APPLICANT_INFO_CARD_WIDTH}
      backgroundColor={'white'}
      borderRadius={'12px'}
      boxShadow={'0px 16px 60px 0px #00000066'}
      alignItems={'stretch'}
      padding={'26px'}
      spacing={'18px'}
    >
      <HStack alignItems={'center'} spacing={'18px'}>
        <Box
          width={'82px'}
          height={'82px'}
          borderRadius={'50%'}
          overflow={'hidden'}
          backgroundColor={'primary.100'}
          flexShrink={0}
        >
          <Image
            src={profile.photoUrl || svgFiles.genericUser}
            alt={'creator-application-profile'}
            width={'100%'}
            height={'100%'}
            objectFit={'cover'}
          />
        </Box>
        <ApplicationDetailSection label={'APPLICANT'}>
          <Text color={'text.darkBlue'} fontSize={'17px'} lineHeight={'22px'} fontWeight={'800'}>
            {applicantName || 'Creator Applicant'}
          </Text>
        </ApplicationDetailSection>
      </HStack>

      {dateLabel && (
        <ApplicationDetailSection label={dateLabel}>
          <Text color={'text.darkBlue'} fontSize={'15px'} lineHeight={'19px'} fontWeight={'700'}>
            {formatContentCreatorApplicationDate(dateValue)}
          </Text>
        </ApplicationDetailSection>
      )}

      {profile.bio && (
        <ApplicationDetailSection label={'BIO'}>
          <Text color={'text.darkBlue'} fontSize={'15px'} lineHeight={'21px'} fontWeight={'700'} maxWidth={'100%'}>
            {profile.bio}
          </Text>
        </ApplicationDetailSection>
      )}

      {locationParts.length > 0 && (
        <ApplicationDetailSection label={'LOCATION'}>
          <Text color={'text.darkBlue'} fontSize={'15px'} lineHeight={'19px'} fontWeight={'700'}>
            {locationParts.join(', ')}
          </Text>
        </ApplicationDetailSection>
      )}

      {contentFocus && (
        <ApplicationDetailSection label={'CONTENT FOCUS'}>
          <Text color={'text.darkBlue'} fontSize={'15px'} lineHeight={'19px'} fontWeight={'700'}>
            {contentFocus}
          </Text>
        </ApplicationDetailSection>
      )}

      {profile.certifications && profile.certifications?.length > 0 && (
        <ApplicationDetailSection label={'CERTIFICATIONS'}>
          <Text color={'text.darkBlue'} fontSize={'15px'} lineHeight={'19px'} fontWeight={'700'}>
            {profile.certifications.join(', ')}
          </Text>
        </ApplicationDetailSection>
      )}

      {profile.socialLinks && profile.socialLinks.length > 0 && (
        <ApplicationDetailSection label={'SOCIAL LINKS'}>
          <VStack alignItems={'flex-start'} spacing={'4px'}>
            {profile.socialLinks.map((socialLink) => (
              <Link
                key={`${socialLink.platform}-${socialLink.url}`}
                href={socialLink.url}
                isExternal
                color={'primary.500'}
                fontSize={'15px'}
                lineHeight={'20px'}
                fontWeight={'800'}
              >
                {socialLink.platform}: {socialLink.url}
              </Link>
            ))}
          </VStack>
        </ApplicationDetailSection>
      )}
    </VStack>
  );
};

export default ApplicantApplicationInfoCard;
